import React, { useState, useEffect, useCallback, memo } from 'react';

const MAX_RETRIES = 3;
const LOAD_TIMEOUT_MS = 25000;

/**
 * Builds a Google Docs Viewer URL from a document URL + a cache-bust key.
 * The retryKey is kept separate from the base URL — no URL string accumulation.
 */
function buildViewerUrl(docUrl, retryKey) {
  const encoded = encodeURIComponent(docUrl);
  return `https://docs.google.com/viewer?url=${encoded}&embedded=true&cb=${retryKey}`;
}

const OfficeIframe = memo(({ url, onLoad, onError }) => (
  <iframe key={url} src={url} className="pmv2-iframe" title="Document viewer" onLoad={onLoad} onError={onError} />
));
OfficeIframe.displayName = 'OfficeIframe';

/**
 * Handles: doc, docx, xls, xlsx, ppt, pptx
 * Rendered via Google Docs Viewer embedded iframe.
 */
const OfficeViewer = ({ document: docUrl, onLoad }) => {
  const [iframeUrl, setIframeUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Effect keyed on docUrl + retryCount: rebuilds viewer URL cleanly each time
  useEffect(() => {
    if (!docUrl) return;
    setIsLoading(true);
    setLoadError(null);
    setIframeUrl(buildViewerUrl(docUrl, `${Date.now()}`));
  }, [docUrl, retryCount]);

  // Timeout guard — no loop
  useEffect(() => {
    if (!isLoading) return;
    const id = setTimeout(() => {
      setIsLoading(false);
      setLoadError('Loading timed out. The document may be too large or unavailable.');
    }, LOAD_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [isLoading]);

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
    setLoadError(null);
    onLoad?.();
  }, [onLoad]);

  const handleIframeError = useCallback(() => {
    setIsLoading(false);
    setLoadError('Failed to load document.');
    if (retryCount < MAX_RETRIES) {
      setRetryCount((prev) => prev + 1);
    }
  }, [retryCount]);

  const handleManualRetry = useCallback(() => {
    setLoadError(null);
    setRetryCount((prev) => (prev >= MAX_RETRIES ? 0 : prev + 1));
  }, []);

  if (!docUrl) return null;

  return (
    <div className="office-viewer">
      {isLoading && (
        <div className="viewer-loading">
          <div className="spinner-border text-primary" role="status" aria-label="Loading document">
            <span className="visually-hidden">Loading document…</span>
          </div>
        </div>
      )}

      {loadError && (
        <div className="viewer-error" role="alert">
          <i className="icon-warning me-2" />
          {loadError}
          <button type="button" className="btn btn-link ms-2 p-0" onClick={handleManualRetry}>
            Try again
          </button>
        </div>
      )}

      {iframeUrl && !loadError && <OfficeIframe url={iframeUrl} onLoad={handleIframeLoad} onError={handleIframeError} />}
    </div>
  );
};

export default React.memo(OfficeViewer);
